import { useMemo, useState, useCallback, useEffect } from "react";
import { Shell } from "./components/layout/Shell";
import { AuthGate } from "./components/layout/AuthGate";
import { CardDetail } from "./components/ui/CardDetail";
import { SettingsSheet } from "./components/ui/SettingsSheet";
import { CommandPalette } from "./components/ui/CommandPalette";
import { ShortcutsHelp } from "./components/ui/ShortcutsHelp";
import { NewTaskDialog } from "./components/ui/NewTaskDialog";
import { DashboardPage } from "./pages/DashboardPage";
import { WorkBoardPage } from "./pages/WorkBoardPage";
import { ChannelsPage } from "./pages/ChannelsPage";
import { ScorecardPage } from "./pages/ScorecardPage";
import { AssetsPage } from "./pages/AssetsPage";
import { ContactsPage } from "./pages/ContactsPage";
import { isWorkBoardRoute } from "./lib/routes";
import { useRouter } from "./hooks/useRouter";
import { useCardSheet } from "./hooks/useCardSheet";
import { useBoardConvex } from "./hooks/useBoardConvex";
import { useShortcuts } from "./hooks/useShortcuts";
import { useTheme } from "./hooks/useTheme";
import { filterCards } from "./lib/boardUtils";
import type { CardStatus } from "./data/types";

export function App() {
  return (
    <AuthGate>
      <AppContent />
    </AuthGate>
  );
}

function AppContent() {
  const { currentRoute, navigate } = useRouter();
  const {
    state,
    setFilters,
    resetFilters,
    toggleChecklist,
    checklistProgress,
    kpiTotals,
    statusCounts,
    updateCardStatus,
    updateCard,
    archiveCard,
    deleteCard,
    createNewCard,
    archiveAllCompleted,
    importCards,
    resetCards,
    isLoading,
  } = useBoardConvex();

  const allCards = useMemo(() => state.lanes.flatMap((l) => l.cards), [state.lanes]);
  const filteredCards = useMemo(() => filterCards(allCards, state.filters), [allCards, state.filters]);

  const { selectedCard, isOpen, openCard, close } = useCardSheet(allCards);

  // ── Sidebar state ─────────────────────────────────────────
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const toggleSidebar = useCallback(() => setSidebarExpanded((p) => !p), []);

  // ── Command palette state ─────────────────────────────────
  const [cmdOpen, setCmdOpen] = useState(false);

  // ── Shortcuts help state ──────────────────────────────────
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  // ── New task dialog state ───────────────────────────────────
  const [newTaskOpen, setNewTaskOpen] = useState(false);

  // ── Settings state ────────────────────────────────────────
  const [settingsOpen, setSettingsOpen] = useState(false);

  // ── Theme ─────────────────────────────────────────────────
  const { theme, resolved: resolvedTheme, setTheme } = useTheme();

  // ── Focus search ──────────────────────────────────────────
  const focusSearch = useCallback(() => {
    const input = document.querySelector<HTMLInputElement>(
      'input[placeholder*="Search"]',
    );
    if (input) {
      input.focus();
      input.select();
    }
  }, []);

  // ── Wire shortcuts ────────────────────────────────────────
  useShortcuts({
    navigate,
    toggleSidebar,
    openCommandPalette: useCallback(() => setCmdOpen(true), []),
    closeSheet: useCallback(() => {
      if (cmdOpen) { setCmdOpen(false); return; }
      if (shortcutsOpen) { setShortcutsOpen(false); return; }
      if (newTaskOpen) { setNewTaskOpen(false); return; }
      if (isOpen) { close(); return; }
    }, [cmdOpen, shortcutsOpen, newTaskOpen, isOpen, close]),
    focusSearch,
    openNewTask: useCallback(() => setNewTaskOpen(true), []),
  });

  // ── ? key for shortcuts help ──────────────────────────────
  // Handled separately because useShortcuts skips inputs
  // and ? is shift+/ which we don't want to conflict
  const handleQuestionMark = useCallback((e: KeyboardEvent) => {
    const tag = (document.activeElement as HTMLElement)?.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
    if (e.key === "?" && !e.metaKey && !e.ctrlKey && !e.altKey) {
      e.preventDefault();
      setShortcutsOpen((p) => !p);
    }
  }, []);

  // Register ? handler
  useEffect(() => {
    window.addEventListener("keydown", handleQuestionMark);
    return () => window.removeEventListener("keydown", handleQuestionMark);
  }, [handleQuestionMark]);

  const handleStatusChange = (cardId: string, status: CardStatus) => {
    updateCardStatus(cardId, status);
  };

  function renderPage() {
    // Board, Path, Timeline, Pipeline, Checklist → unified Work Board with tabs
    if (isWorkBoardRoute(currentRoute)) {
      return (
        <WorkBoardPage
          activeTab={currentRoute as "board" | "path" | "timeline" | "checklist" | "pipeline"}
          onTabChange={navigate}
          filteredCards={filteredCards}
          allCards={allCards}
          filters={state.filters}
          onFilterChange={setFilters}
          onResetFilters={resetFilters}
          onSelectCard={openCard}
          onStatusChange={handleStatusChange}
          kpiTotals={kpiTotals}
        />
      );
    }

    switch (currentRoute) {
      case "dashboard":
        if (isLoading) {
          return (
            <div className="h-full flex items-center justify-center">
              <p className="text-[14px] text-[var(--color-text-muted)]">Loading board…</p>
            </div>
          );
        }
        return (
          <DashboardPage
            cards={allCards}
            kpiTotals={kpiTotals}
            statusCounts={statusCounts}
            checklistItems={state.checklist}
            checklistProgress={checklistProgress}
            onToggleChecklist={toggleChecklist}
            onSelectCard={openCard}
            onNavigate={navigate}
          />
        );
      case "channels":
        if (isLoading) {
          return (
            <div className="h-full flex items-center justify-center">
              <p className="text-[14px] text-[var(--color-text-muted)]">Loading board…</p>
            </div>
          );
        }
        return (
          <ChannelsPage
            cards={allCards}
            onSelectCard={openCard}
          />
        );
      case "contacts":
        return <ContactsPage />;
      case "scorecard":
        if (isLoading) {
          return (
            <div className="h-full flex items-center justify-center">
              <p className="text-[14px] text-[var(--color-text-muted)]">Loading board…</p>
            </div>
          );
        }
        return (
          <ScorecardPage kpis={state.kpis} />
        );
      case "assets":
        return <AssetsPage />;
      default:
        if (isLoading) {
          return (
            <div className="h-full flex items-center justify-center">
              <p className="text-[14px] text-[var(--color-text-muted)]">Loading board…</p>
            </div>
          );
        }
        return (
          <DashboardPage
            cards={allCards}
            kpiTotals={kpiTotals}
            statusCounts={statusCounts}
            checklistItems={state.checklist}
            checklistProgress={checklistProgress}
            onToggleChecklist={toggleChecklist}
            onSelectCard={openCard}
            onNavigate={navigate}
          />
        );
    }
  }

  return (
    <>
      <Shell
        currentRoute={currentRoute}
        onNavigate={navigate}
        statusCounts={statusCounts}
        checklistProgress={checklistProgress}
        sidebarExpanded={sidebarExpanded}
        onToggleSidebar={toggleSidebar}
        onOpenSettings={() => setSettingsOpen(true)}
        onOpenCommandPalette={() => setCmdOpen(true)}
      >
        {renderPage()}
      </Shell>

      <CardDetail
        card={selectedCard}
        allCards={allCards}
        open={isOpen}
        onOpenChange={(open) => { if (!open) close(); }}
        onStatusChange={handleStatusChange}
        onCardUpdate={updateCard}
        onArchive={(cardId) => { archiveCard(cardId); close(); }}
        onDelete={(cardId) => { deleteCard(cardId); close(); }}
      />

      <SettingsSheet
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        cards={allCards}
        onImportCards={importCards}
        onResetCards={resetCards}
        theme={theme}
        resolvedTheme={resolvedTheme}
        onThemeChange={setTheme}
      />

      <CommandPalette
        open={cmdOpen}
        onOpenChange={setCmdOpen}
        onNavigate={navigate}
        onToggleSidebar={toggleSidebar}
        onShowShortcuts={() => setShortcutsOpen(true)}
        onCreateTask={() => setNewTaskOpen(true)}
        onFilterOwner={(owner) => {
          if (owner === "all") resetFilters();
          else setFilters({ owner });
        }}
        onArchiveCompleted={() => { archiveAllCompleted(); }}
      />

      <ShortcutsHelp
        open={shortcutsOpen}
        onClose={() => setShortcutsOpen(false)}
      />

      <NewTaskDialog
        open={newTaskOpen}
        onOpenChange={setNewTaskOpen}
        onCreate={createNewCard}
      />
    </>
  );
}
